import { useCallback, useEffect, useRef } from 'react';

import { useWebsocketSubscription, WebsocketConnectMeta } from './useWebsocketSubscription';

type UseDebouncedWebsocketSubscriptionOptions<T> = {
  channel: string;
  params?: Record<string, unknown>;
  // Forwarded verbatim to useWebsocketSubscription, which passes the connect metadata; expose it
  // so callers can implement the joinedExisting handshake-skip (useWorkflowNudge's pattern).
  onConnected?: (meta: WebsocketConnectMeta) => void;
  onDisconnected?: () => void;
  onReceived?: (messages: T[]) => void;
  onRejected?: () => void;
  enabled?: boolean;
  debounceMs: number;
};

export const useDebouncedWebsocketSubscription = <T,>({
  channel,
  params,
  onConnected,
  onDisconnected,
  onReceived,
  onRejected,
  enabled,
  debounceMs,
}: UseDebouncedWebsocketSubscriptionOptions<T>) => {
  const pendingMessages = useRef<T[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onReceivedRef = useRef(onReceived);

  onReceivedRef.current = onReceived;

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleReceived = useCallback(
    (data: T) => {
      pendingMessages.current.push(data);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        onReceivedRef.current?.(pendingMessages.current);
        pendingMessages.current = [];
        timerRef.current = null;
      }, debounceMs);
    },
    [debounceMs]
  );

  return useWebsocketSubscription<T>({
    channel,
    params,
    onConnected,
    onDisconnected,
    onReceived: handleReceived,
    onRejected,
    enabled,
  });
};
